import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { organizationService } from '../services';
import { Trash2, Pencil, Plus, Building2, Search, MapPin, XCircle, Loader2, Users, CreditCard, Layout, Mail, ShieldCheck, Globe, Calendar, Eye, Filter } from 'lucide-react';
import { Pagination, DetailsSidebar, Modal, Select } from '../components';
import { planService } from '../services';

/**
 * Organization lifecycle stages, manually tracked by admins.
 * Colours follow the agreed scheme:
 *   Setup → grey, Trial → amber, Active → green,
 *   Suspended → orange-red, Archived → dark grey, Deleted → red.
 */
const ORG_STATUSES = [
  { value: 'SETUP', label: 'Setup', pill: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-[#31373f] dark:text-gray-300 dark:border-gray-700' },
  { value: 'TRIAL', label: 'Trial', pill: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/10 dark:text-amber-400 dark:border-amber-900/50' },
  { value: 'ACTIVE', label: 'Active', pill: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/10 dark:text-green-400 dark:border-green-900/50' },
  { value: 'SUSPENDED', label: 'Suspended', pill: 'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/10 dark:text-orange-400 dark:border-orange-900/50' },
  { value: 'ARCHIVED', label: 'Archived', pill: 'bg-slate-200 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700' },
  { value: 'DELETED', label: 'Deleted', pill: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/10 dark:text-red-400 dark:border-red-900/50' },
];
const STATUS_MAP = Object.fromEntries(ORG_STATUSES.map((s) => [s.value, s]));
// Legacy rows created before the status column existed fall back to is_active.
const getOrgStatus = (org) => org.status || (org.is_active ? 'ACTIVE' : 'SUSPENDED');

/**
 * Organizations List Page
 * Balanced, moderately compact design with colorful capacity chips and view action
 */
const Organizations = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Why the last delete failed — shown in the confirm box, so a failed delete
  // doesn't replace the whole list with an error.
  const [deleteError, setDeleteError] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(null);

  // Filter States
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    organization_type: '',
    plan_id: ''
  });
  const [activeFilters, setActiveFilters] = useState({
    status: '',
    organization_type: '',
    plan_id: ''
  });
  const [plans, setPlans] = useState([]);

  // Set to a reasonable number for standard screens
  const LIMIT = 15;

  /**
   * Fetch organizations list
   */
  const fetchOrganizations = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await organizationService.getAllOrganizations({
        page,
        limit: LIMIT,
        search: submittedSearch,
        status: activeFilters.status,
        organization_type: activeFilters.organization_type,
        plan_id: activeFilters.plan_id
      });

      if (response.success) {
        setOrganizations(response.data || []);
        setTotalPages(response.pagination?.pages || 1);
      } else {
        setError('Failed to load organizations');
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError(err.message || 'Error loading organizations');
    } finally {
      setLoading(false);
    }
  }, [submittedSearch, activeFilters]);

  /**
   * Fetch Available Plans for Filter
   */
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await planService.getAllPlans();
        if (response.success) {
          setPlans(response.data || []);
        }
      } catch (err) {
        console.error('Error fetching plans:', err);
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    fetchOrganizations(currentPage);
  }, [fetchOrganizations, currentPage]);

  /**
   * Handle Search
   */
  const handleSearch = (e) => {
    e.preventDefault();
    setSubmittedSearch(searchTerm);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSubmittedSearch('');
    setFilters({ status: '', organization_type: '', plan_id: '' });
    setActiveFilters({ status: '', organization_type: '', plan_id: '' });
    setCurrentPage(1);
  };

  const applyFilters = () => {
    setActiveFilters(filters);
    setCurrentPage(1);
    setShowFilterModal(false);
  };

  const hasActiveFilters = activeFilters.status !== '' || activeFilters.organization_type !== '' || activeFilters.plan_id !== '';

  /**
   * Change an organization's lifecycle stage inline from the list.
   */
  const handleStatusChange = async (org, newStatus) => {
    if (newStatus === getOrgStatus(org)) return;
    try {
      setStatusUpdating(org.id);
      const response = await organizationService.updateOrganizationStatus(org.id, newStatus);
      if (response.success) {
        setOrganizations((prev) =>
          prev.map((o) =>
            o.id === org.id
              ? { ...o, status: newStatus, is_active: response.data?.is_active ?? o.is_active }
              : o
          )
        );
      }
    } catch (err) {
      console.error('Error updating organization status:', err);
      setError(err.message || 'Failed to update status');
    } finally {
      setStatusUpdating(null);
    }
  };

  /**
   * Handle Delete
   */
  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      setDeleteError('');
      const response = await organizationService.deleteOrganization(deleteConfirm.id);
      if (response.success) {
        setDeleteConfirm(null);
        fetchOrganizations(currentPage);
      } else {
        setDeleteError(response.message || 'Could not delete this organisation.');
      }
    } catch (err) {
      console.error('Error deleting organization:', err);
      // The service rethrows the API's JSON body, so its message is the real reason.
      setDeleteError(err?.message && err.message !== 'Internal server error'
        ? err.message
        : 'Could not delete this organisation. Nothing was deleted — please try again or contact support.');
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Show Organization Details in Sidebar
   */
  const handleShowDetails = async (id) => {
    try {
      setLoadingDetails(true);
      setSidebarOpen(true);
      const response = await organizationService.getOrganizationById(id);
      if (response.success && response.data) {
        const orgData = response.data;
        // Normalize menu_ids for all users
        if (orgData.Users) {
          orgData.Users = orgData.Users.map(u => {
            if (u.UserMenu && typeof u.UserMenu.menu_ids === 'string') {
              try {
                u.UserMenu.menu_ids = JSON.parse(u.UserMenu.menu_ids);
              } catch (e) {
                u.UserMenu.menu_ids = [];
              }
            }
            return u;
          });
        }
        setSelectedOrg(orgData);
      }
    } catch (err) {
      console.error('Error fetching org details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <div className="space-y-4 pb-10 w-full animate-in fade-in duration-500 font-sans">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-3">
        <div>
          <h1 className="text-[22px] font-semibold text-[#1E293B] dark:text-gray-100 tracking-tight">Organizations</h1>
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-0.5">
            Manage your service locations and operational data
          </p>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-white dark:bg-[#262b33] rounded-[12px] shadow-sm border border-[#E2E8F0] dark:border-gray-800 overflow-hidden">

        {/* Search & Filter Bar */}
        <div className="px-5 py-3.5 border-b border-[#E2E8F0] dark:border-gray-800 flex flex-col sm:flex-row justify-end items-center gap-3 bg-white dark:bg-[#262b33]">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row items-center gap-2.5 w-full sm:w-auto">
            <div className="relative w-full sm:w-[300px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-8 py-2 border border-[#CBD5E1] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB] text-[13px] text-[#0F172A] dark:text-gray-200 transition-colors bg-[#F8FAFC] dark:bg-[#31373f]"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] dark:text-gray-500 hover:text-[#64748B]"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex w-full sm:w-auto gap-2">
              <button
                type="submit"
                className="bg-[#2563EB] hover:bg-blue-700 text-white px-3.5 py-2 rounded-lg transition-all shadow-sm flex items-center justify-center group shrink-0"
                title="Search"
              >
                <Search className="w-4 h-4 group-hover:scale-110 transition-transform" />
              </button>

              <button
                type="button"
                onClick={() => setShowFilterModal(true)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border transition-all text-[13px] font-medium shrink-0 ${hasActiveFilters
                  ? 'bg-blue-50 border-blue-200 text-blue-600 dark:bg-blue-900/20 dark:border-blue-900/50 dark:text-blue-400'
                  : 'bg-white border-[#CBD5E1] text-[#475569] hover:bg-gray-50 dark:bg-[#31373f] dark:border-gray-700 dark:text-gray-400 dark:hover:bg-[#31373f]'
                  }`}
              >
                <Filter className={`w-4 h-4 ${hasActiveFilters ? 'fill-blue-600/10' : ''}`} />
                <span>Filters</span>
                {hasActiveFilters && (
                  <span className="flex items-center justify-center w-4 h-4 text-[10px] bg-blue-600 text-white rounded-full">
                    {[activeFilters.status, activeFilters.organization_type, activeFilters.plan_id].filter(Boolean).length}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate('/organizations/create')}
                className="flex items-center gap-2 bg-[#2563EB] text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition shadow-sm text-[13px] font-medium shrink-0"
              >
                <Plus className="w-4 h-4" />
                Add Organization
              </button>
            </div>
          </form>
        </div>

        {/* Table Area */}
        <div className="relative min-h-[350px]">
          {loading ? (
            <div className="absolute inset-0 bg-white/50 dark:bg-[#262b33]/50 backdrop-blur-[1px] z-10 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-7 h-7 animate-spin text-[#2563EB]" />
                <p className="text-[13px] font-medium text-[#64748B] dark:text-gray-400">Loading...</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <div className="bg-red-50 border border-red-100 text-red-600 dark:bg-red-900/10 dark:border-red-900/50 dark:text-red-400 px-5 py-4 rounded-lg inline-block text-[13px]">
                {error}
              </div>
              <button
                onClick={() => fetchOrganizations(currentPage)}
                className="block mx-auto mt-4 text-[13px] text-blue-600 font-medium hover:underline"
              >
                Try Again
              </button>
            </div>
          ) : organizations.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#F8FAFC] dark:bg-[#31373f] border-b border-[#E2E8F0] dark:border-gray-800">
                    <th className="text-left px-5 py-3 text-[12px] font-semibold text-[#64748B] dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Organization</th>
                    <th className="text-left px-5 py-3 text-[12px] font-semibold text-[#64748B] dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Type</th>
                    <th className="text-left px-5 py-3 text-[12px] font-semibold text-[#64748B] dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Contact</th>
                    <th className="text-left px-5 py-3 text-[12px] font-semibold text-[#64748B] dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Capacity</th>
                    <th className="text-left px-5 py-3 text-[12px] font-semibold text-[#64748B] dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="text-right px-5 py-3 text-[12px] font-semibold text-[#64748B] dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]/60 dark:divide-gray-800">
                  {organizations.map((org) => (
                    <tr key={org.id} className="hover:bg-[#F8FAFC]/80 dark:hover:bg-[#31373f] transition-colors group">

                      {/* Organization Name & Details */}
                      <td className="px-5 py-3">
                        <div className="cursor-pointer group/name inline-block" onClick={() => handleShowDetails(org.id)}>
                          <div className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200 group-hover/name:text-[#2563EB] transition-colors flex items-center gap-2">
                            {org.name}
                            {/* Multi Site Group: how many sites are switched on. */}
                            {org.site_count > 1 && (
                              <span className="text-[10px] font-bold uppercase tracking-wide text-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 dark:text-indigo-300 px-1.5 py-0.5 rounded border border-indigo-100 dark:border-indigo-900/50 whitespace-nowrap">
                                {org.site_count} sites
                              </span>
                            )}
                            <div className="opacity-0 group-hover/name:opacity-100 transition-opacity text-[10px] bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:border-blue-900/50 px-1.5 py-0.5 rounded border border-blue-100 font-bold uppercase whitespace-nowrap">
                              View Details
                            </div>
                          </div>
                          <div className="text-[12px] text-[#64748B] dark:text-gray-400 flex items-center gap-1.5 mt-1">
                            <span className="font-mono font-medium text-blue-600/80 uppercase tracking-tight shrink-0">
                              {org.org_id || `ID#${org.id}`}
                            </span>
                            <span className="text-[#CBD5E1] dark:text-gray-600 font-bold">·</span>
                            <span className="truncate max-w-[280px]">
                              {org.address || 'No address provided'}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-5 py-3 text-left">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-900/50 uppercase tracking-wide">
                          {org.organization_type || 'Unknown'}
                        </span>
                      </td>

                      {/* Contact */}
                      <td className="px-5 py-3 text-left">
                        <div className="text-[13px] text-[#475569] dark:text-gray-400">{org.email || '-'}</div>
                        <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-0.5">{org.phone || '-'}</div>
                      </td>

                      {/* Capacity - Colorful Horizontal Chips.
                          Domiciliary orgs have no beds, so we show live counts
                          of Users (service users / residences) + Staff and let
                          them click through to the org details. Care homes keep
                          the bed/resident capacity view. */}
                      <td className="px-5 py-3 text-left">
                        {org.organization_type === 'domiciliary' ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Users (service users / residences) Chip */}
                            <button
                              type="button"
                              onClick={() => handleShowDetails(org.id)}
                              title="View service users"
                              className="flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 border border-purple-100 dark:bg-purple-900/10 dark:border-purple-900/50 rounded-md whitespace-nowrap hover:bg-purple-100 dark:hover:bg-purple-900/20 transition-colors cursor-pointer"
                            >
                              <span className="text-[10px] font-bold text-purple-700 dark:text-purple-400 uppercase tracking-wide">Users</span>
                              <span className="text-[12px] font-semibold text-purple-600 dark:text-purple-400">
                                {org.service_user_count || 0}
                              </span>
                            </button>

                            {/* Staff Chip */}
                            <button
                              type="button"
                              onClick={() => handleShowDetails(org.id)}
                              title="View staff"
                              className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-100 dark:bg-amber-900/10 dark:border-amber-900/50 rounded-md whitespace-nowrap hover:bg-amber-100 dark:hover:bg-amber-900/20 transition-colors cursor-pointer"
                            >
                              <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wide">Staffs</span>
                              <span className="text-[12px] font-semibold text-amber-600 dark:text-amber-400">
                                {org.staff_count || 0}
                              </span>
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Residents Chip — live count of linked residences */}
                            <button
                              type="button"
                              onClick={() => handleShowDetails(org.id)}
                              title="View residents"
                              className="flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 border border-purple-100 dark:bg-purple-900/10 dark:border-purple-900/50 rounded-md whitespace-nowrap hover:bg-purple-100 dark:hover:bg-purple-900/20 transition-colors cursor-pointer"
                            >
                              <span className="text-[10px] font-bold text-purple-700 dark:text-purple-400 uppercase tracking-wide">Residents</span>
                              <span className="text-[12px] font-semibold text-purple-600 dark:text-purple-400">
                                {org.service_user_count || 0}
                              </span>
                            </button>

                            {/* Staff Chip — live count of linked staff */}
                            <button
                              type="button"
                              onClick={() => handleShowDetails(org.id)}
                              title="View staff"
                              className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-100 dark:bg-amber-900/10 dark:border-amber-900/50 rounded-md whitespace-nowrap hover:bg-amber-100 dark:hover:bg-amber-900/20 transition-colors cursor-pointer"
                            >
                              <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wide">Staff</span>
                              <span className="text-[12px] font-semibold text-amber-600 dark:text-amber-400">
                                {org.staff_count || 0}
                              </span>
                            </button>
                          </div>
                        )}
                      </td>

                      {/* Status — inline lifecycle stage selector */}
                      <td className="px-5 py-3 text-left">
                        {(() => {
                          const current = getOrgStatus(org);
                          const busy = statusUpdating === org.id;
                          return (
                            <Select
                              value={current}
                              onChange={(v) => handleStatusChange(org, v)}
                              options={ORG_STATUSES.map((s) => ({ value: s.value, label: s.label }))}
                              disabled={busy}
                              size="sm"
                              className="w-[150px]"
                            />
                          );
                        })()}
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleShowDetails(org.id)}
                            className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/organizations/${org.id}`)}
                            className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => { setDeleteError(''); setDeleteConfirm(org); }}
                            className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-20 flex flex-col items-center justify-center text-center px-4">
              <div className="w-14 h-14 bg-[#F1F5F9] dark:bg-[#31373f] rounded-full flex items-center justify-center mb-4">
                <Building2 className="w-7 h-7 text-[#94A3B8] dark:text-gray-500" />
              </div>
              <h3 className="text-[15px] font-semibold text-[#1E293B] dark:text-gray-100 mb-1">No organizations found</h3>
              <p className="text-[13px] text-[#64748B] dark:text-gray-400 mb-5 max-w-sm">
                {submittedSearch
                  ? "We couldn't find any results matching your search."
                  : "Start by adding your first service location."}
              </p>
              {submittedSearch && (
                <button
                  onClick={clearFilters}
                  className="text-[#2563EB] font-medium text-[13px] hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination Section */}
        {organizations.length > 0 && (
          <div className="border-t border-[#E2E8F0] dark:border-gray-800 p-3 bg-[#F8FAFC] dark:bg-[#20242c]">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-[#0F172A]/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4 font-sans animate-in fade-in duration-200">
          <div className="bg-white dark:bg-[#262b33] rounded-[12px] shadow-xl p-6 max-w-[380px] w-full animate-in zoom-in-95 duration-200">
            <div className="w-12 h-12 bg-red-50 dark:bg-red-900/10 rounded-full flex items-center justify-center mb-4 border border-red-100 dark:border-red-900/50">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <h3 className="text-[18px] font-bold text-[#1E293B] dark:text-gray-100 mb-2">Delete Organization?</h3>
            <p className="text-[14px] text-[#475569] dark:text-gray-400 mb-6 leading-relaxed">
              Delete <strong>{deleteConfirm.name}</strong>? This permanently deletes everything in it — {deleteConfirm.site_count > 1 ? 'all its sites, ' : ''}the owner, managers and staff, every resident / client, all care records, rotas and visits, and every uploaded file. It cannot be undone.
            </p>
            {deleteError && (
              <p role="alert" className="-mt-3 mb-5 rounded-lg border border-red-100 dark:border-red-900/50 bg-red-50 dark:bg-red-900/10 px-3 py-2.5 text-[13px] text-red-700 dark:text-red-400">
                {deleteError}
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2.5 border border-[#CBD5E1] dark:border-gray-700 rounded-lg text-[13px] font-medium text-[#475569] dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2.5 bg-[#EF4444] text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2 text-[13px] font-medium shadow-sm shadow-red-500/20"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                {deleting ? 'Deleting...' : 'Delete Organization'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Sidebar */}
      <DetailsSidebar
        isOpen={sidebarOpen}
        onClose={() => {
          setSidebarOpen(false);
          setSelectedOrg(null);
        }}
        title={selectedOrg?.name || 'Organization Details'}
        icon={Building2}
      >
        {loadingDetails ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <p className="text-[13px] font-medium text-[#64748B] dark:text-gray-400">Fetching complete profile...</p>
          </div>
        ) : selectedOrg ? (
          <div className="space-y-8 animate-in fade-in duration-300">

            {/* 1. Basic Information Section */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-[#1E293B] dark:text-gray-200 border-b border-gray-100 dark:border-gray-800 pb-2">
                <Building2 className="w-4 h-4 text-blue-500" />
                <h3 className="text-[14px] font-bold uppercase tracking-wider">Business Identity</h3>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-[#20242c] border border-gray-100 dark:border-gray-800">
                  <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase mb-1">Org ID</p>
                  <p className="text-[12px] font-mono font-bold text-blue-600 uppercase">{selectedOrg.org_id || `ID#${selectedOrg.id}`}</p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-[#20242c] border border-gray-100 dark:border-gray-800">
                  <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase mb-1">Type</p>
                  <p className="text-[12px] font-semibold text-gray-700 dark:text-gray-200 capitalize">{selectedOrg.organization_type}</p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-[#20242c] border border-gray-100 dark:border-gray-800">
                  <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase mb-1">CQC Status</p>
                  <p className="text-[12px] font-semibold text-gray-700 dark:text-gray-200">{selectedOrg.cqc_registration_number || 'N/A'}</p>
                </div>
              </div>
              <div className="space-y-3 px-1">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#31373f] flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase">Headquarters</p>
                    <p className="text-[13px] text-gray-600 dark:text-gray-400 leading-relaxed">{selectedOrg.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#31373f] flex items-center justify-center shrink-0">
                    <Globe className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase">Contact Portal</p>
                    <p className="text-[13px] text-gray-600 dark:text-gray-400 leading-tight">{selectedOrg.email}</p>
                    <p className="text-[13px] text-gray-600 dark:text-gray-400 mt-0.5">{selectedOrg.phone}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* 2. Operational Capacity */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-[#1E293B] dark:text-gray-200 border-b border-gray-100 dark:border-gray-800 pb-2">
                <Layout className="w-4 h-4 text-blue-500" />
                <h3 className="text-[14px] font-bold uppercase tracking-wider">Operations & Capacity</h3>
              </div>
              {/* Live counts of linked residences and staff (beds removed). */}
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 rounded-xl bg-purple-50/50 border border-purple-100 dark:bg-purple-900/10 dark:border-purple-900/50">
                  <p className="text-[18px] font-bold text-purple-700 dark:text-purple-400">{selectedOrg.service_user_count || 0}</p>
                  <p className="text-[10px] font-bold text-purple-500 dark:text-purple-400 uppercase">
                    {selectedOrg.organization_type === 'domiciliary' ? 'Service Users' : 'Residents'}
                  </p>
                </div>
                <div className="text-center p-3 rounded-xl bg-orange-50/50 border border-orange-100 dark:bg-orange-900/10 dark:border-orange-900/50">
                  <p className="text-[18px] font-bold text-orange-700 dark:text-orange-400">{selectedOrg.staff_count || 0}</p>
                  <p className="text-[10px] font-bold text-orange-500 dark:text-orange-400 uppercase">Staff Size</p>
                </div>
              </div>
            </section>

            {/* 3. Managers Information */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-[#1E293B] dark:text-gray-200 border-b border-gray-100 dark:border-gray-800 pb-2">
                <Users className="w-4 h-4 text-blue-500" />
                <h3 className="text-[14px] font-bold uppercase tracking-wider">Administrative Access</h3>
              </div>
              <div className="space-y-3">
                {selectedOrg.Users && selectedOrg.Users.length > 0 ? (
                  selectedOrg.Users.map((user) => (
                    <div key={user.id} className="p-4 rounded-2xl bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 shadow-sm flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 font-bold border border-blue-100 dark:border-blue-900/50 shrink-0 uppercase">
                        {user.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <h4 className="text-[14px] font-bold text-gray-800 dark:text-gray-200 truncate">{user.name}</h4>
                          <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 dark:bg-blue-900/20 dark:border-blue-900/50 uppercase">
                            {user.Role?.role_name || 'Manager'}
                          </span>
                        </div>
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center gap-2 text-[12px] text-gray-500 dark:text-gray-400">
                            <Mail className="w-3.5 h-3.5" /> {user.email || 'No email'}
                          </div>
                          <div className="flex items-center gap-2 text-[12px] text-gray-500 dark:text-gray-400">
                            <ShieldCheck className="w-3.5 h-3.5" /> ID: <span className="font-mono">{user.user_id}</span>
                          </div>
                        </div>
                        {user.UserMenu?.menu_names && Array.isArray(user.UserMenu.menu_names) && user.UserMenu.menu_names.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {user.UserMenu.menu_names.map((name, idx) => (
                              <span key={idx} className="text-[9px] font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 dark:bg-[#31373f] dark:text-gray-400 dark:border-gray-700 uppercase">
                                {name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center py-6 text-gray-400 dark:text-gray-500 text-[13px]">No administrative users assigned</p>
                )}
              </div>
            </section>

            {/* 4. Subscription & Plan */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-[#1E293B] dark:text-gray-200 border-b border-gray-100 dark:border-gray-800 pb-2">
                <CreditCard className="w-4 h-4 text-blue-500" />
                <h3 className="text-[14px] font-bold uppercase tracking-wider">Subscription Tier</h3>
              </div>
              <div className="p-5 rounded-2xl bg-gradient-to-br from-[#1E293B] to-[#334155] text-white shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-10">
                  <CreditCard className="w-24 h-24" />
                </div>
                <div className="relative z-10">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Active Plan</p>
                      <h4 className="text-[20px] font-bold tracking-tight">
                        {selectedOrg.activePlan?.plan?.name || 'Free Trial'}
                      </h4>
                    </div>
                    <span className="bg-blue-500 text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest shadow-lg shadow-blue-500/30">
                      {selectedOrg.activePlan?.subscription_status || 'Trial'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-6 mt-8 pt-6 border-t border-white/10">
                    <div>
                      <p className="text-[10px] font-bold text-white/50 uppercase mb-1">Billing Cycle</p>
                      <p className="text-[13px] font-semibold flex items-center gap-2 uppercase">
                        <Calendar className="w-3.5 h-3.5" /> {selectedOrg.activePlan?.billing_cycle || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-white/50 uppercase mb-1">Expires On</p>
                      <p className="text-[13px] font-semibold">
                        {selectedOrg.activePlan?.trial_end_date ? new Date(selectedOrg.activePlan.trial_end_date).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

          </div>
        ) : null}
      </DetailsSidebar>
      {/* Filter Modal */}
      <Modal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        title="Filter Organizations"
        maxWidth="max-w-md"
        footer={
          <div className="flex gap-3 justify-end w-full">
            <button
              onClick={() => {
                setFilters({ status: '', organization_type: '', plan_id: '' });
                setActiveFilters({ status: '', organization_type: '', plan_id: '' });
                setShowFilterModal(false);
              }}
              className="px-4 py-2 text-[13px] font-medium text-[#64748B] dark:text-gray-400 hover:text-[#1E293B] dark:hover:text-gray-200 transition-colors"
            >
              Reset All
            </button>
            <button
              onClick={applyFilters}
              className="px-6 py-2 bg-[#2563EB] text-white rounded-lg hover:bg-blue-700 transition shadow-sm text-[13px] font-medium"
            >
              Apply Filters
            </button>
          </div>
        }
      >
        <div className="space-y-5">
          {/* Status Filter — lifecycle stages */}
          <div className="space-y-2">
            <label className="text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Status</label>
            <div className="grid grid-cols-2 gap-2">
              {ORG_STATUSES.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setFilters({ ...filters, status: filters.status === s.value ? '' : s.value })}
                  className={`py-2 px-3 rounded-lg border text-[13px] font-medium transition-all ${filters.status === s.value
                    ? s.pill
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 dark:bg-[#31373f] dark:border-gray-700 dark:text-gray-400 dark:hover:bg-[#31373f]'
                    }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Organization Type Filter */}
          <div className="space-y-2">
            <label className="text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Organization Type</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setFilters({ ...filters, organization_type: 'carehome' })}
                className={`py-2 px-3 rounded-lg border text-[13px] font-medium transition-all ${filters.organization_type === 'carehome'
                  ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-900/50 dark:text-blue-400'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 dark:bg-[#31373f] dark:border-gray-700 dark:text-gray-400 dark:hover:bg-[#31373f]'
                  }`}
              >
                Care Home
              </button>
              <button
                onClick={() => setFilters({ ...filters, organization_type: 'domiciliary' })}
                className={`py-2 px-3 rounded-lg border text-[13px] font-medium transition-all ${filters.organization_type === 'domiciliary'
                  ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-900/50 dark:text-blue-400'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 dark:bg-[#31373f] dark:border-gray-700 dark:text-gray-400 dark:hover:bg-[#31373f]'
                  }`}
              >
                Domiciliary
              </button>
            </div>
          </div>

          {/* Plan Filter */}
          <div className="space-y-2">
            <label className="text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Subscription Plan</label>
            <Select
              value={filters.plan_id}
              onChange={(v) => setFilters({ ...filters, plan_id: v })}
              options={[
                { value: '', label: 'All Plans' },
                ...plans.map(plan => ({ value: String(plan.id), label: plan.name })),
              ]}
              className="w-full"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Organizations;